// @ts-check

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, DataTable, InlineStack, Link, Spinner, Text, Tooltip } from "@shopify/polaris";
import { SearchResourceIcon } from "@shopify/polaris-icons";

import { SyncingPlaceholder } from "components/SyncingPlaceholder/SyncingPlaceholder";

import { useGlobalContext } from "context/GlobalContext";

import { useFormatCurrency } from "hooks/use-format-currency";

const NUM_RESULTS = 5;
const TRUNCATE_LENGTH = 30;

export const TopMetricsCard = ({
  metricTitle,
  metricKey,
  calculateViewAllHref,
  calculateReportHref,
  calculateOrdersHref,
}) => {
  const {
    syncing,
    topMetrics,
    isLoadingMetrics,
  } = useGlobalContext();
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();

  const data = useMemo(() => {
    if (!topMetrics || topMetrics.error) {
      return [];
    }

    const realData = topMetrics.data[metricKey].data
      ?.map((metric) => {
        let metricValue = metric.metric_value;
        const shouldTruncateValue = metric.metric_value && metric.metric_value.length > TRUNCATE_LENGTH;
  
        if (shouldTruncateValue) {
          metricValue = (
            <Text as="span">
              {metricValue.substring(0, TRUNCATE_LENGTH).trim()}...
            </Text>
          );
        }

        if (calculateReportHref) {
          const handleClick = () => {
            const href = calculateReportHref(metric.metric_value);
            navigate(href);
          }

          metricValue = (
            <Link
              key={metric.metric_value}
              onClick={handleClick}
              removeUnderline
            >
              {metricValue}
            </Link>
          );
        }

        let metricCount = metric.metric_count;
        if (calculateOrdersHref) {
          const handleClick = () => {
            const href = calculateOrdersHref(metric.metric_value);
            navigate(href);
          }

          metricCount = (
            <Link
              key={metric.metric_value}
              onClick={handleClick}
              removeUnderline
            >
              {metricCount}
            </Link>
          );
        }

        if (shouldTruncateValue) {
          metricValue = (
            <Tooltip content={metric.metric_value} dismissOnMouseOut>
              {metricValue}
            </Tooltip>
          );
        }

        return [
          metricValue,
          metricCount,
          <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>{formatCurrency(metric.total_price_sum)}</Text>,
        ];
      }) || [];

    const placeholderData = Array.from({ length: NUM_RESULTS - realData.length }, (v, i) => ([
      "-",
      "-",
      "-",
    ]));

    return [...realData, ...placeholderData];
  }, [topMetrics]);

  const handleViewMore = () => {
    const href = calculateViewAllHref();
    navigate(href);
  };

  let spinner;
  if (isLoadingMetrics) {
    spinner = <Spinner size="small" />;
  }

  let viewAllButton;
  if (calculateViewAllHref) {
    viewAllButton = (
      <Button
        icon={SearchResourceIcon}
        variant="tertiary"
        onClick={handleViewMore}
        accessibilityLabel="View more"
        disabled={syncing}
      >
        View all
      </Button>
    );
  }

  let footerContent;
  if (topMetrics?.error) {
    footerContent = "Error";
  } else if (topMetrics) {
    footerContent = `Showing ${topMetrics.data[metricKey].data.length} of ${topMetrics.data[metricKey].totalCount}`;
  }

  return (
    <Card padding="0">
      <Box padding="300">
        <InlineStack align="space-between">
          <Text as="h2" variant="headingSm">Top {metricTitle}s</Text>

          <InlineStack align="space-between" gap="200">
            {spinner}

            {viewAllButton}
          </InlineStack>
        </InlineStack>
      </Box>

      <SyncingPlaceholder>
        <DataTable
          columnContentTypes={[
            "text",
            "numeric",
            "numeric",
          ]}
          headings={[
            metricTitle,
            "Orders",
            "Revenue",
          ]}
          rows={data}
          footerContent={footerContent}
        />
      </SyncingPlaceholder>
    </Card>
  );
};
